import i18n from '@common/i18n.js';
import {ANSWER_TYPE, PARENT_COUNSEL_WEEK_NO_TIME_OPTION} from '@common/consts/survey.js';
import {toISODateFormat} from '@common/utils/date.js';
import {createAnswer, createQuestion} from '@common/store/modules/survey/editor.js';
import {getNextSequence} from '@/iamteacherpc/utils/sequence.js';

const DEFAULT_COUNSEL_PERIOD_COUNT = 5;
const DEFAULT_COUNSEL_TIME_SLOT_COUNT = 4;
const DEFAULT_COUNSEL_TIME_SLOT_VALUE = '14:00~14:30';

export const MAX_COUNSEL_PERIOD = 30;
export const MAX_COUNSEL_TIME_SLOT = 20;

const SUNDAY = 0;
const MONDAY = 1;
const SATURDAY = 6;

export const ADD_QUESTION_COUNSEL_PERIOD = 'ADD_QUESTION_COUNSEL_PERIOD';
export const REMOVE_QUESTION_COUNSEL_PERIOD = 'REMOVE_QUESTION_COUNSEL_PERIOD';
export const SET_QUESTION_COUNSEL_PERIOD_COUNT = 'SET_QUESTION_COUNSEL_PERIOD_COUNT';
export const SET_QUESTION_COUNSEL_PERIOD_VALUE = 'SET_QUESTION_COUNSEL_PERIOD_VALUE';
export const GET_QUESTION_COUNSEL_PERIOD_RESET_DATES = 'GET_QUESTION_COUNSEL_PERIOD_RESET_DATES';

export const ADD_QUESTION_COUNSEL_TIME_SLOT = 'ADD_QUESTION_COUNSEL_TIME_SLOT';
export const REMOVE_QUESTION_COUNSEL_TIME_SLOT = 'REMOVE_QUESTION_COUNSEL_TIME_SLOT';
export const SET_QUESTION_COUNSEL_TIME_SLOT_COUNT = 'SET_QUESTION_COUNSEL_TIME_SLOT_COUNT';
export const SET_QUESTION_COUNSEL_TIME_SLOT_VALUE = 'SET_QUESTION_COUNSEL_TIME_SLOT_VALUE';

export const SET_QUESTION_COUNSEL_TIME_VALUE = 'SET_QUESTION_COUNSEL_TIME_VALUE';

export const SET_QUESTION_COUNSEL_NO_TIME_OPTION = 'SET_QUESTION_COUNSEL_NO_TIME_OPTION';

export const mutations = {
    addQuestionCounselPeriod(state, {question}) {
        const periodCount = question.counselPeriods.length;
        if (periodCount >= MAX_COUNSEL_PERIOD) {
            return;
        }

        addCounselPeriod({question, restDays: state.restDays});
    },

    removeQuestionCounselPeriod(state, {question, index}) {
        if (question.counselPeriods.length <= 1) {
            return;
        }

        question.counselPeriods.splice(index, 1);
        question.counselTimes.splice(index, 1);
    },

    setQuestionCounselPeriodCount(state, {question, count}) {
        if (count <= 0 || count > MAX_COUNSEL_PERIOD) {
            return;
        }

        const periodCount = question.counselPeriods.length;

        if (count < periodCount) {
            question.counselPeriods = question.counselPeriods.slice(0, count);
            question.counselTimes = question.counselTimes.slice(0, count);
            return;
        }

        const diff = count - periodCount;
        for (let i = 0; i < diff; i++) {
            addCounselPeriod({question, restDays: state.restDays});
        }
    },

    setQuestionCounselPeriodValue(state, {question, index, value}) {
        question.counselPeriods[index].value = value;
    },

    addQuestionCounselTimeSlot(state, {question}) {
        const timeSlotCount = question.counselTimeSlots.length;
        if (timeSlotCount >= MAX_COUNSEL_TIME_SLOT) {
            return;
        }

        addCounselTimeSlot({question});
    },

    removeQuestionCounselTimeSlot(state, {question, index}) {
        if (question.counselTimeSlots.length <= 1) {
            return;
        }

        question.counselTimeSlots.splice(index, 1);
        for (let i = 0; i < question.counselTimes.length; i++) {
            question.counselTimes[i].splice(index, 1);
        }
    },

    setQuestionCounselTimeSlotCount(state, {question, count}) {
        if (count <= 0 || count > MAX_COUNSEL_TIME_SLOT) {
            return;
        }

        const timeSlotCount = question.counselTimeSlots.length;

        if (count < timeSlotCount) {
            question.counselTimeSlots = question.counselTimeSlots.slice(0, count);
            for (let i = 0; i < question.counselTimes.length; i++) {
                question.counselTimes[i] = question.counselTimes[i].slice(0, count);
            }
            return;
        }

        const diff = count - timeSlotCount;
        for (let i = 0; i < diff; i++) {
            addCounselTimeSlot({question});
        }
    },

    setQuestionCounselTimeSlotValue(state, {question, index, value}) {
        question.counselTimeSlots[index] = value;
        question.counselTimeSlots = [...question.counselTimeSlots]; // 반응형으로 동작하기 위하여 레퍼런스 교체
    },

    setQuestionCounselTimeValue(state, {question, periodIndex, timeSlotIndex, value}) {
        question.counselTimes[periodIndex][timeSlotIndex].value = value;
    },

    setQuestionCounselNoTimeOption(state, {question, noTimeOption}) {
        question.noTimeOption = noTimeOption;

        if (noTimeOption === PARENT_COUNSEL_WEEK_NO_TIME_OPTION.USE_TEXT) {
            question.questions[0].isUseQuestion = true;
        } else {
            question.questions[0].isUseQuestion = false;
        }
    },
}

export const actions = {
    [ADD_QUESTION_COUNSEL_PERIOD]({commit}, {question}) {
        commit('addQuestionCounselPeriod', {question});
    },

    [REMOVE_QUESTION_COUNSEL_PERIOD]({commit}, {question, index}) {
        commit('removeQuestionCounselPeriod', {question, index});
    },

    [SET_QUESTION_COUNSEL_PERIOD_COUNT]({commit}, {question, count}) {
        commit('setQuestionCounselPeriodCount', {question, count});
    },

    [ADD_QUESTION_COUNSEL_TIME_SLOT]({commit}, {question}) {
        commit('addQuestionCounselTimeSlot', {question});
    },

    [REMOVE_QUESTION_COUNSEL_TIME_SLOT]({commit}, {question, index}) {
        commit('removeQuestionCounselTimeSlot', {question, index});
    },

    [SET_QUESTION_COUNSEL_TIME_SLOT_COUNT]({commit}, {question, count}) {
        commit('setQuestionCounselTimeSlotCount', {question, count});
    },

    [SET_QUESTION_COUNSEL_TIME_SLOT_VALUE]({commit}, {question, index, value}) {
        commit('setQuestionCounselTimeSlotValue', {question, index, value});
    },

    [SET_QUESTION_COUNSEL_TIME_VALUE]({commit}, {question, periodIndex, timeSlotIndex, value}) {
        commit('setQuestionCounselTimeValue', {question, periodIndex, timeSlotIndex, value});
    },

    [SET_QUESTION_COUNSEL_PERIOD_VALUE]({commit}, {question, index, value}) {
        commit('setQuestionCounselPeriodValue', {question, index, value});
    },

    [GET_QUESTION_COUNSEL_PERIOD_RESET_DATES]({state}, {question, startDate}) {
        const resetDates = [startDate];

        let date = startDate;
        for (let i = 1/* 첫 날짜는 추가되어 있음 */; i < question.counselPeriods.length; i++) {
            date = getNextDate({
                date,
                restDays: state.restDays,
            });
            resetDates.push(date);
        }

        return resetDates;
    },

    [SET_QUESTION_COUNSEL_NO_TIME_OPTION]({commit}, {question, noTimeOption}) {
        commit('setQuestionCounselNoTimeOption', {question, noTimeOption});
    },
}

export function createParentCounselWeekQuestion({restDays}) {
    function createCounselTypeQuestion() {
        const question = createQuestion();

        question.mandatory = true;

        const answer1 = createAnswer(question);
        answer1.text = i18n.t('SurveyQuestionEditorParentCounselWeek_countType_tel');

        const answer2 = createAnswer(question);
        answer2.text = i18n.t('SurveyQuestionEditorParentCounselWeek_countType_offline');

        question.type = ANSWER_TYPE.ONE_CHOICE;
        question.question = i18n.t('SurveyQuestionEditorParentCounselWeek_pleaseSelectCounselType');
        question.answers = [
            answer1,
            answer2,
        ];

        return question;
    }

    function createCounselTimeQuestion({restDays}) {

        function getTodayDateWithZeroTime() {
            const today = new Date();
            today.setHours(0);
            today.setMinutes(0);
            today.setSeconds(0);
            today.setMilliseconds(0);
            return today;
        }

        function getNextMondayDate({restDays}) {
            const today = getTodayDateWithZeroTime();
            const date = today.getDate();
            const day = today.getDay();
            const diffDay = MONDAY + 7 - day;

            const candidateDate = new Date(today.getTime());
            candidateDate.setDate(date + diffDay);

            return getWorkingDayDate({date: candidateDate, restDays});
        }

        const noTimeQuestion = {
            ...createQuestion(),
            question: i18n.t('SurveyQuestionEditorParentCounselWeekTime_pleaseInputTime'),
            type: ANSWER_TYPE.TEXT,
        }

        const question = {
            ...createQuestion(),
            mandatory: true,
            type: ANSWER_TYPE.PARENT_COUNSEL_WEEK_TIME,
            question: i18n.t('SurveyQuestionEditorParentCounselWeekTime_pleaseSelectTime'),
            counselPeriods: [],
            counselTimeSlots: [],
            counselTimes: [],
            noTimeOption: PARENT_COUNSEL_WEEK_NO_TIME_OPTION.USE_TEXT,
            questions: [noTimeQuestion],
        };

        noTimeQuestion.parentQuestion = question;

        let date = getNextMondayDate({restDays});
        const counselPeriods = [{
            id: getNextSequence(),
            value: date,
        }];

        for (let i = 0; i < DEFAULT_COUNSEL_PERIOD_COUNT - 1/*첫 날짜가 이미 들어감*/; i++) {
            date = getNextDate({date, restDays});
            counselPeriods.push({
                id: getNextSequence(),
                value: date,
            });
        }

        question.counselPeriods = counselPeriods;
        question.counselTimeSlots = Array(DEFAULT_COUNSEL_TIME_SLOT_COUNT);
        question.counselTimeSlots[0] = DEFAULT_COUNSEL_TIME_SLOT_VALUE;

        question.counselTimes = [];
        for (let i = 0; i < question.counselPeriods.length; i++) {
            const times = [];
            for (let j = 0; j < question.counselTimeSlots.length; j++) {
                times.push({
                    id: getNextSequence(),
                    value: 1,
                });
            }
            question.counselTimes.push(times);
        }

        // 사용자에게 불가능 지정 가능 예시 보여줌
        question.counselTimes[1][1].value = 0;
        question.counselTimes[3][2].value = 0;

        return question;
    }

    function createCounselAgendaQuestion() {
        const question = createQuestion();

        question.mandatory = true;

        const answer1 = createAnswer(question);
        answer1.text = i18n.t('SurveyQuestionEditorParentCounselWeekAgenda_answer1');

        const answer2 = createAnswer(question);
        answer2.text = i18n.t('SurveyQuestionEditorParentCounselWeekAgenda_answer2');

        const answer3 = createAnswer(question);
        answer3.text = i18n.t('SurveyQuestionEditorParentCounselWeekAgenda_answer3');

        const answer4 = createAnswer(question);
        answer4.text = i18n.t('SurveyQuestionEditorParentCounselWeekAgenda_answer4');

        question.type = ANSWER_TYPE.MULTI_CHOICE;
        question.question = i18n.t('SurveyQuestionEditorParentCounselWeekAgenda_question');
        question.answers = [
            answer1,
            answer2,
            answer3,
            answer4,
        ];

        return question;
    }

    function createCounselAdditionalQuestion() {
        return {
            ...createQuestion(),
            question: i18n.t('SurveyQuestionEditorParentCounselWeekAdditional_question'),
            type: ANSWER_TYPE.TEXT,
        };
    }

    return {
        ...createQuestion(),
        questionNo: 0,
        type: ANSWER_TYPE.PARENT_COUNSEL_WEEK,
        questions: [
            createCounselTypeQuestion(),
            createCounselTimeQuestion({restDays}),
            createCounselAgendaQuestion(),
            createCounselAdditionalQuestion(),
        ],
    }
}

function getNextDate({date, restDays}) {
    const copiedDate = new Date(date.getTime());
    copiedDate.setDate(copiedDate.getDate() + 1);
    return getWorkingDayDate({date: copiedDate, restDays});
}

function getWorkingDayDate({date, restDays}) {
    const copiedDate = new Date(date.getTime());

    const day = copiedDate.getDay();
    if (day === SUNDAY) {
        copiedDate.setDate(copiedDate.getDate() + 1);
        return getWorkingDayDate({date: copiedDate, restDays});
    }

    if (day === SATURDAY) {
        copiedDate.setDate(copiedDate.getDate() + 2);
        return getWorkingDayDate({date: copiedDate, restDays});
    }

    const dateIso = toISODateFormat(copiedDate);

    if (restDays.some(r => r === dateIso)) {
        copiedDate.setDate(copiedDate.getDate() + 1);
        return getWorkingDayDate({date: copiedDate, restDays});
    }

    return copiedDate;
}

function addCounselPeriod({question, restDays}) {
    const lastDate = question.counselPeriods[question.counselPeriods.length - 1].value;
    const nextDate = getNextDate({
        date: lastDate,
        restDays,
    });

    question.counselPeriods.push({
        id: getNextSequence(),
        value: nextDate,
    });

    const times = [];
    for (let i = 0; i < question.counselTimeSlots.length; i++) {
        times.push({
            id: getNextSequence(),
            value: 1,
        });
    }
    question.counselTimes.push(times);
}

function addCounselTimeSlot({question}) {
    question.counselTimeSlots.push('');

    for (let i = 0; i < question.counselPeriods.length; i++) {
        question.counselTimes[i].push({
            id: getNextSequence(),
            value: 1,
        });
    }
}